import {useBynder} from '@/hooks/useBynder'
import {InfoOrError} from '@/shared-components/InfoOrError.tsx'
import {Spinner} from '@/shared-components/Spinner'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'pepsico-ds'
import {useCallback, useEffect, useState} from 'react'
import {FileUploadField} from './FileUploadField'
import './imageUploader.scss'

export type ImageUploadChangeValue = string | {id: string; url: string; title: string}

interface SingleImageUploadProps {
  value?: string
  onChange: (value: ImageUploadChangeValue) => void
  label?: string
  required?: boolean
  disabled?: boolean
  accept?: string
  maxWidth?: number
  maxHeight?: number
  maxSize?: number // in KB
  aspectRatio?: string // e.g., "16:9"
  requiredResolution?: boolean
  description?: string
  shortDescription?: string
  onUpload?: (file: File) => Promise<string> // Optional custom upload handler
  enableGallery?: boolean // Enable gallery selection
  galleryTag?: string // Tag to filter gallery images
}

interface FileData {
  base64?: string
  name: string
  thumb?: string
}

export const SingleImageUploadControl = ({
  value,
  onChange,
  disabled = false,
  required = false,
  accept,
  maxWidth,
  maxHeight,
  maxSize = 10000, // 10MB default
  aspectRatio,
  requiredResolution = false,
  description,
  shortDescription,
  onUpload,
  enableGallery = true,
  galleryTag,
}: SingleImageUploadProps) => {
  const [file, setFile] = useState<FileData>({base64: '', name: value || '', thumb: value})
  const [loader, setLoader] = useState(false)
  const [, setUploadProgress] = useState(0)
  const [error, setError] = useState<{msg: string; tryAgainButton: boolean}>({msg: '', tryAgainButton: false})

  const queryClient = useQueryClient()
  const {upload: uploadToBynder} = useBynder()

  const ratio = aspectRatio?.split(':')
  const ratioWidth = Number(ratio?.[0] ?? 1)
  const ratioHeight = Number(ratio?.[1] ?? 1)
  const ratioValue = ratioWidth / ratioHeight

  const handleFileUpload = useCallback(
    async (newFile: File | string, newFileName?: string, assetId?: string) => {
      // Handle selection from gallery (string URL)
      if (typeof newFile === 'string') {
        if (!newFile) {
          setError({msg: 'Selected asset does not have a valid image URL.', tryAgainButton: false})
          return
        }
        setFile({thumb: newFile, name: newFileName || newFile, base64: undefined})
        onChange(assetId ? {id: assetId, url: newFile, title: newFileName || ''} : newFile)
        return
      }

      // Validate file type
      const fileExtension = newFile.name.split('.').pop()
      const hasIncludeExt = !accept || accept.includes(`.${fileExtension}`)

      if (!hasIncludeExt) {
        setError({
          msg: `Invalid file type! Allowed file types are ${accept}`,
          tryAgainButton: false,
        })
        return
      }

      // Validate file size
      if (newFile.size && Math.ceil(newFile.size / 1000) > maxSize) {
        const isMb = maxSize >= 1000
        const size = isMb ? maxSize / 1000 : maxSize
        const sizeUnit = isMb ? 'MB' : 'KB'
        setError({
          msg: `Image is too large! Maximum size allowed is ${size}${sizeUnit}.`,
          tryAgainButton: false,
        })
        return
      }

      setError({msg: '', tryAgainButton: false})

      // Convert file to base64 for preview
      const reader = new FileReader()
      reader.readAsDataURL(newFile)
      reader.onload = async () => {
        setFile({base64: reader.result as string, name: newFile.name})

        // Use custom upload handler if provided, otherwise upload to Bynder
        if (onUpload) {
          try {
            setLoader(true)
            const uploadedUrl = await onUpload(newFile)
            setLoader(false)
            if (uploadedUrl) {
              onChange(uploadedUrl)
            } else {
              setError({msg: 'Upload failed', tryAgainButton: true})
            }
          } catch (err) {
            console.error(err)
            setLoader(false)
            setError({msg: 'Something went wrong', tryAgainButton: true})
          }
        } else {
          // Default: Upload to Bynder
          try {
            setLoader(true)
            setUploadProgress(0)

            const result = (await uploadToBynder({
              file: newFile,
              title: newFile.name,
              updatePercentage: setUploadProgress,
              tags: galleryTag,
            })) as Record<string, string>

            setLoader(false)
            setUploadProgress(0)

            if (result?.original) {
              onChange({id: result.id, url: result.original, title: result.name})
              queryClient.invalidateQueries({queryKey: ['bynder-assets']})
            } else {
              setError({msg: 'Upload failed - no URL returned', tryAgainButton: true})
            }
          } catch (err) {
            console.error('Bynder upload error:', err)
            setLoader(false)
            setUploadProgress(0)
            setError({msg: err instanceof Error ? err.message : 'Upload failed', tryAgainButton: true})
          }
        }
      }
    },
    [accept, maxSize, onUpload, uploadToBynder, onChange, galleryTag, queryClient]
  )

  const retryUpload = useCallback(async () => {
    if (!file?.base64) return

    try {
      setLoader(true)
      setUploadProgress(0)

      // Convert base64 back to file for retry
      const response = await fetch(file.base64)
      const blob = await response.blob()
      const fileObj = new File([blob], file.name, {type: blob.type})

      if (onUpload) {
        const uploadedUrl = await onUpload(fileObj)
        setLoader(false)
        if (uploadedUrl) {
          onChange(uploadedUrl)
          setError({msg: '', tryAgainButton: false})
        } else {
          setError({msg: 'Something went wrong', tryAgainButton: true})
        }
      } else {
        // Default: Upload to Bynder
        const result = (await uploadToBynder({
          file: fileObj,
          title: fileObj.name,
          updatePercentage: setUploadProgress,
        })) as Record<string, string>

        setLoader(false)
        setUploadProgress(0)

        if (result?.original) {
          onChange({id: result.id, url: result.original, title: result.name})
          setError({msg: '', tryAgainButton: false})
        } else {
          setError({msg: 'Upload failed - no URL returned', tryAgainButton: true})
        }
      }
    } catch (err) {
      console.error('Retry upload error:', err)
      setLoader(false)
      setUploadProgress(0)
      setError({msg: err instanceof Error ? err.message : 'Something went wrong', tryAgainButton: true})
    }
  }, [file, onUpload, uploadToBynder, onChange])

  const cleanImage = () => {
    setError({msg: '', tryAgainButton: false})
    setFile({base64: '', name: ''})
    onChange('')
  }

  const imgLoadHandler = (evt: React.SyntheticEvent<HTMLImageElement>) => {
    setError({msg: '', tryAgainButton: false})

    const target = evt.target as HTMLImageElement
    const naturalWidth = target.naturalWidth
    const naturalHeight = target.naturalHeight

    // Validate aspect ratio
    if (aspectRatio) {
      const requiredRatioValue = Math.round((ratioValue + 0.0001) * 100) / 100
      const imageRatioValue = Math.round((naturalWidth / naturalHeight + 0.0001) * 100) / 100

      if (requiredRatioValue !== imageRatioValue) {
        setError({
          msg: `Image aspect ratio with ${ratioWidth}:${ratioHeight} is required!`,
          tryAgainButton: false,
        })
        return
      }
    } else {
      // Validate exact dimensions
      if (requiredResolution && (naturalWidth !== maxWidth || naturalHeight !== maxHeight)) {
        setError({
          msg: `Image resolution with ${maxWidth}px width and ${maxHeight}px height is required!`,
          tryAgainButton: false,
        })
        return
      }

      // Validate max allowed width
      if (maxWidth && naturalWidth > maxWidth) {
        setError({
          msg: `Maximum ${maxWidth}px image width allowed!`,
          tryAgainButton: false,
        })
        return
      }

      // Validate max allowed height
      if (maxHeight && naturalHeight > maxHeight) {
        setError({
          msg: `Maximum ${maxHeight}px image height allowed!`,
          tryAgainButton: false,
        })
        return
      }
    }

    // Image is valid
    // Note: If using Bynder upload (no custom handler), the URL is already set in handleFileUpload
    // If using custom handler, the URL is already set there as well
    // If neither (base64 mode), we set it here
    if (!onUpload && !value && !loader) {
      // Only set base64 if no URL has been set yet and no upload is in progress (backwards compatibility)
      onChange(file.thumb || file.base64 || '')
    }
  }

  useEffect(() => {
    if (value && value.length) {
      setFile(prev => {
        // Keep the local base64 preview if we already have one (e.g. just uploaded).
        // Only load the remote URL on fresh mount with a pre-existing value.
        if (prev.base64) return prev
        return {thumb: value, name: value, base64: ''}
      })
    } else {
      setFile({thumb: '', name: '', base64: ''})
    }
  }, [value])

  return (
    <>
      {file.name && (
        <div className="images-container">
          <img
            data-testid="image-thumbnail"
            src={file?.base64 ? file.base64 || value : file?.thumb}
            alt={file?.name}
            onLoad={imgLoadHandler}
            style={{
              width: '100%',
              height: '200px',
              cursor: 'pointer',
              objectFit: 'contain',
            }}
          />
          <div className={loader ? 'imageOverlay loader' : 'imageOverlay'}>
            {loader ? (
              <Spinner />
            ) : (
              <Button
                onClick={cleanImage}
                style={{backgroundColor: '#117D9F'}}
                size="medium"
                iconTrailing="autorenew"
                disabled={disabled}
              >
                Replace Image
              </Button>
            )}
          </div>
        </div>
      )}
      {error.msg && (
        <div className="display-flex align-items-center justify-content-center mt-2 gap-2">
          <span style={{color: 'red', fontSize: '14px'}}>{error.msg}</span>
          {error.tryAgainButton && (
            <Button variant="secondary" onClick={retryUpload}>
              Try again
            </Button>
          )}
        </div>
      )}
      {!file.name && (
        <FileUploadField
          className="fileUploader"
          updateValue={handleFileUpload}
          maxSize={maxSize}
          acceptedExtensions={accept}
          disabled={disabled}
          hasError={required && !value}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          aspectRatio={aspectRatio}
          description={description}
          shortDescription={shortDescription}
          enableGallery={enableGallery}
          galleryTag={galleryTag}
        />
      )}
      {required && !value && !error.msg && <InfoOrError error="Image is required" id="image-upload" />}
    </>
  )
}
